import React, { Fragment, useEffect, useLayoutEffect, useState } from 'react';
import logo from './logo.svg';
import axios, { AxiosRequestConfig } from "axios";
import Product from './types/product';
import './App.css';
import { IPublicClientApplication } from '@azure/msal-browser';
import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import { CustomNavigationClient } from "./utils/NavigationClient";
import { billingRequest, loginRequest } from './authConfig';
import Home from "./pages/Home";
import BillingPage from './pages/Billing';
import vh from './utilities/vhFull';
import NotFound from './pages/NotFound';
import UploadReportsPage from './pages/UploadReports';
import BillingAnalyticsPage from './pages/BillingAnalytics';
import ClientSchedulePage from './pages/ClientSchedule';
import SessionSchedulePage from './pages/SessionSchedule';
import CoverageShiftForm from './pages/CoverageShiftForm';
import SessionForm from './pages/SessionForm';
import CoverageRequirementForm from './pages/CoverageRequirementForm';
import ClientRequirementPage from './pages/ClientRequirement';
import RequirementCalendarPage from './pages/RequirementCalendar';
import NewCoverageRequirementForm from './pages/NewCoverageRequirement';
import LegacySchedulePage from './pages/LegacySchedule';
import LandingPage from './pages/LandingPage';
import { AppProvider, useAppContext } from './Context';
import Core from './templates/Core';
import ClientManagementPage from './pages/ClientManagement';
import NewClientPage from './pages/NewClientForm';
import TestPage from './pages/TestPage';
import EditClientPage from './pages/EditClientPage';
import NewServiceLocationPage from './pages/NewServiceLocationForm';
import NewServicePlanPage from './pages/NewServicePlanForm';
import ChatGPTPage from './pages/ChatGPTPage';
import EditServicePlanPage from './pages/EditServicePlanPage';
import ClientSpanRequirementPage from './pages/Scheduling/ClientRequirement';
import StaffShortageCalendar from './pages/HR/StaffShortageCalendar';
import ClientScheduleManagement from './pages/Scheduling/ClientScheduleManagement';
import TimeOffPlannerCalendar from './pages/HR/TimeOffRequestCalendar';
import RSASchedulingPage from './pages/Scheduling/RSAScheduling';
import ScheduleDashboard from './pages/Scheduling/ScheduleDashboard';
import ErrorPage from './pages/ErrorPage';
import ErrorBoundary from './ErrorBoundary';
import OperationAnalyticsPage from './pages/Analytics/OperationAnalytics';
import AvailabilityForm from './pages/Availability/AvailabilityForm';
import AvailabilityPeriodsPage from './pages/Availability/AvailabilityPeriodsPage';
import AvailabilityResponsesPage from './pages/Availability/AvailabilityResponsesPage';
import AvailabilityPeriodSubmissionsPage from './pages/Availability/AvailabilityPeriodSubmissionsPage';
import AvailabilitySubmission from './pages/Availability/AvailabilitySubmission';
import DirectSupportClientsList from './pages/DirectSupport/DirectSupportClientsList';
import DirectSupportWeeklySchedule from './pages/DirectSupport/DirectSupportWeeklySchedule';
import SchedulingCopilotPage from './pages/Scheduling/SchedulingCopilot';
import AgencyAnnouncementsPage from './pages/Announcements/AgencyAnnouncementsPage';
import ClientAnnouncementsPage from './pages/Announcements/ClientAnnouncementsPage';
import AddAgencyAnnouncementPage from './pages/Announcements/AddAgencyAnnouncementPage';
import AddClientAnnouncementPage from './pages/Announcements/AddClientAnnouncementPage';
import ManageAgencyAnnouncementPage from './pages/Announcements/ManageAgencyAnnouncementPage';
import ManageClientAnnouncementPage from './pages/Announcements/ManageClientAnnouncementPage';
import CoPilotSandboxPage from './pages/CoPilotSandboxPage';
import ProviderSessionSchedulePage from './pages/Provider/SessionSchedule';
import RemoteSupportTicketPage from './pages/Provider/RemoteSupportTicketPage';
import TrainingLandingPage from './pages/Training/TrainingLandingPage';
import CreateTrainingPage from './pages/Training/CreateTrainingPage';
import TrainingPreviewPage from './pages/Training/TrainingPreviewPage';
import ContinuousEducationLandingPage from './pages/Training/ContinuousEducationLandingPage';
import ProviderTrainingLandingPage from './pages/Training/ProviderTrainingLandingPage';
import ProviderCompleteTrainingPage from './pages/Training/ProviderTrainingPage';
import ManageClientTrainingPage from './pages/Training/ManageClientTrainingPage';
import CoPilotFeedSandboxPage from './pages/CoPilotFeedSanboxPage';
import CreateContinuousTrainingPage from './pages/Training/CreateContinuousTrainingPage';
import ManageContinuousEducationPage from './pages/Training/ManageContinuousEducationPage';
import EmployeeContinuousEducationPage from './pages/Training/EmployeeContinuousEducationPage';
import EmpContEdLandingPage from './pages/Training/EmpContEdLandingPage';
import AdminTicketingDashboard from './pages/Ticketing/AdminTicketingDashboard';
import ControlCenterPage from './pages/Administration/ControlCenterPage';
import BackgroundJobsPage from './pages/Administration/BackgroundJobsPage';
import RSReportManagementPage from './pages/RSReportManagement/RSReportManagementPage';
import ClientReportManagementPage from './pages/RSReportManagement/ClientReportManagementPage';
import OutcomeTrackingPage from './pages/Administration/OutcomeTrackingPage';
import MonitoringDashboardPage from './pages/Monitoring/MonitoringDashboardPage';
import VideoPage from './pages/Thermal/VideoPage';
import VideoAnnotationPage from './pages/Thermal/VideoAnnotationPage';
import Participants from './pages/Thermal/Participants';
import ParticipantDetailPage from './pages/Thermal/ParticipantDetailPage';
import SupervisorCalendar, { SupervisorCalendarForm } from './pages/Scheduling/SupervisorCalendar';
import TrainingDashboardPage from './pages/Training/TrainingDashboardPage';

declare global {
  interface Window {
    dataLayer: any;
  }
}

export type AppProps = {
  pca: IPublicClientApplication
};

function App({ pca }: AppProps) {
  const navContext = useAppContext();
  const [hideNav, setHideNav] = React.useState(false);

  const location = useLocation();

  const history = useNavigate();
  const navigationClient = new CustomNavigationClient(history);
  pca.setNavigationClient(navigationClient);

  useLayoutEffect(() => {
		vh(); // get current page height
    
	}, []);

	useEffect(() => {
    //setHideNav( location.pathname === '/' ? true : false);
		vh();
	}, [location]);

  const handleLogout = () => {
    pca.logoutRedirect();
  }

  const authProps = {
		handleLogout
	}

  const sharedAttributes = {
		setHideNav, hideNav, ...authProps
	}

  function randomGen() {
    throw new Error('Function not implemented.');
  }

  //const navContext = useAppContext();

	// const location = useLocation();
	// // The next 3-4 lines are optional. This is how you configure MSAL to take advantage of the router's navigate functions when MSAL redirects between pages in your app
	// // const history = useHistory();
	// const history = useNavigate();
	// const navigationClient = new CustomNavigationClient(history);
	// pca.setNavigationClient(navigationClient);
	// const [hideNav, setHideNav] = useState(false);

	// useLayoutEffect(() => {
	// 	vh(); // get current page height
	// }, []);

	// useEffect(() => {
	// 	setHideNav( location.pathname === '/' ? true : false);
	// 	vh();
	// }, [location]);


	// const authProps = {
	// 	setHideNav, hideNav
	// }

  const navigate = useNavigate();

  return (
    <AppProvider>
         <MsalProvider instance={pca}>

              <AuthenticatedTemplate>
                <Core hideNav={ hideNav } {...sharedAttributes}>
                
                  <Routes>
                    <Route path="*" element={<NotFound />} />
                    <Route path="/error" element={<ErrorPage />} />
                    <Route path="/admin/monitoring" element={<MonitoringDashboardPage {...authProps} />} /> /* Done */
                    <Route path="/admin/outcome-tracking" element={<OutcomeTrackingPage {...authProps} />} /> /* Done */
                    <Route path="/admin/rs-report-management" element={<RSReportManagementPage { ...authProps } />} /> /* Done */
                    <Route path="/admin/client-report-management" element={<ClientReportManagementPage { ...authProps } />} /> /* Done */
                    <Route path="/schedule/schedule-mgmt/:patientID/weekly-schedule" element={<ClientScheduleManagement { ...authProps } key={Math.random()}/>} /> /* Done */
                    <Route path="/schedule/schedule-mgmt/:patientID/weekly-schedule/:weekStart/:weekEnd" element={<ClientScheduleManagement { ...authProps } key={Math.random()}/>} /> /* Done */
                    <Route path="/billing/claims" element={<BillingPage { ...authProps } />} /> /* Done */
                    <Route path="/billing/upload-reports" element={<UploadReportsPage { ...authProps } />} /> /* Done */
                    <Route path="/billing/analytics" element={<BillingAnalyticsPage { ...authProps } />} /> /* Done */
                    <Route path="/billing/metrics" element={<OperationAnalyticsPage { ...authProps } />} /> /* Done */
                    <Route path="/legacy/schedule/client-schedule" element={<ClientSchedulePage { ...authProps }/>}/> /* Done */
                    <Route path="/admin/schedule/co-pilot" element={<SchedulingCopilotPage { ...authProps }/>}/> /* Done */
                    <Route path="/schedule/client-schedule/:patientID/legacy" element={<LegacySchedulePage { ...authProps } />} /> /* Done */
                    <Route path="/schedule/client-schedule/:patientID/new" element={<CoverageShiftForm { ...authProps } />} /> /* Done */
                    <Route path="/schedule/client-requirement" element={<ClientRequirementPage { ...authProps } />} /> /* Done */
                    <Route path="/schedule/client-requirement/:patientID/requirement" element={<RequirementCalendarPage { ...authProps } />} /> /* Done */
                    <Route path="/schedule/client-requirement/:patientID/requirement/:weekStart/:weekEnd" element={<RequirementCalendarPage { ...authProps } key={Math.random()}/>} /> /* Done */
                    <Route path="/schedule/client-requirement/:patientID/requirement-form" element={<CoverageRequirementForm { ...authProps } />} /> /* Done */
                    <Route path="/schedule/client-requirement/:patientID/span-requirement" element={<ClientSpanRequirementPage { ...authProps } />} /> /* Done */
                    <Route path="/schedule/client-requirement/:patientID/New" element={<NewCoverageRequirementForm { ...authProps } />} /> /* Done */    
                    <Route path="/legacy/schedule/direct-support" element={<DirectSupportClientsList { ...authProps }/>}/> /* Done */   
                    <Route path="/legacy/schedule/direct-support/:patientID/weekly-schedule" element={<DirectSupportWeeklySchedule { ...authProps } key={Math.random()}/>} /> /* Done */
                    <Route path="/legacy/schedule/direct-support/:patientID/weekly-schedule/:weekStart/:weekEnd" element={<DirectSupportWeeklySchedule { ...authProps } key={Math.random()}/>} /> /* Done */         
                    <Route path="/schedule/session-schedule" element={<SessionSchedulePage { ...authProps } />} /> /* Done */
                    <Route path="/schedule/session-schedule/:weekStart/:weekEnd" element={<SessionSchedulePage { ...authProps } key={Math.random()}/>} /> /* Done */
                    <Route path="/schedule/session-schedule/new" element={<SessionForm { ...authProps } />} /> /* Done */
                    <Route path="/schedule/session-calendar" element={<RSASchedulingPage { ...authProps } />} /> /* Done */
                    <Route path="/schedule/session-calendar/:calendarType" element={<RSASchedulingPage {...sharedAttributes} />} /> /* Done */

                    <Route path="/schedule/dashboard" element={<ScheduleDashboard {...sharedAttributes} />} /> /* Done */
                    <Route path="/client/client-management" element={<ClientManagementPage />} /> /* Done */
                    <Route path="/client/add" element={<NewClientPage />} /> /* Done */
                    <Route path="/client/:clientID" element={<EditClientPage />} /> /* Done */
                    <Route path="/client/:clientID/serviceLocation/new" element={<NewServiceLocationPage />} /> /* Done */
                    <Route path="/client/:clientID/servicePlan/new" element={<NewServicePlanPage />} /> /* Done */
                    <Route path="/client/:clientID/servicePlan/:planID/edit" element={<EditServicePlanPage />} /> /* Done */
                    <Route path="/client/chat" element={<ChatGPTPage />} /> /* Done */
                    <Route path="/client/co-pilot" element={<CoPilotSandboxPage {...authProps} />} key={Math.random()}/> /* Done */
                    <Route path="/client/co-pilot-feed" element={<CoPilotFeedSandboxPage {...authProps} />} key={Math.random()}/> /* Done */
                    <Route path="/client/co-pilot/:chatID" element={<CoPilotSandboxPage {...authProps} />} key={Math.random()}/> /* Done */
                    <Route path="/hr/coverage-planner" element={<StaffShortageCalendar />} /> /* Done */
                    <Route path="/hr/time-off-planner" element={<TimeOffPlannerCalendar />} /> /* Done */
                    <Route path="/" element={<LandingPage { ...authProps } />} /> /* Done */
                    <Route path="/testing" element={<TestPage {...authProps} />} /> /* Done */
                    <Route path="/schedule/availability/providerAvailability" element={<AvailabilityPeriodsPage {...authProps} />} /> /* Done */
                    <Route path="/availability/providerAvailability" element={<AvailabilityPeriodsPage {...authProps} />} /> /* Done */
                    <Route path="/availability/submissions" element={<AvailabilityResponsesPage {...authProps} />} /> /* Done */
                    <Route path="/availability/providerAvailability/:periodID/form" element={<AvailabilityForm {...authProps} />} /> /* Done */
                    <Route path="/availability/submissions/:periodID/submissions" element={<AvailabilityPeriodSubmissionsPage {...authProps} />} /> /* Done */
                    <Route path="/availability/submissions/:periodID/submissions/:submissionID" element={<AvailabilitySubmission {...authProps} />} /> /* Done */

                    <Route path="/announcement/agency" element={<AgencyAnnouncementsPage {...authProps} />} /> /* Done */
                    <Route path="/announcement/agency/add" element={<AddAgencyAnnouncementPage {...authProps} />} /> /* Done */
                    <Route path="/announcement/agency/:announcementID/edit" element={<ManageAgencyAnnouncementPage {...authProps} />} /> /* Done */
                    <Route path="/announcement/clients" element={<ClientAnnouncementsPage {...authProps} />} /> /* Done */
                    <Route path="/announcement/clients/add" element={<AddClientAnnouncementPage {...authProps} />} /> /* Done */
                    <Route path="/announcement/clients/:announcementID/edit" element={<ManageClientAnnouncementPage {...authProps} />} /> /* Done */

                    <Route path="/schedule/provider/schedule/session-calendar/:weekStart/:weekEnd" element={<ProviderSessionSchedulePage {...authProps} />} key={Math.random()}/> /* Done */
                    <Route path="/schedule/provider/schedule/session-calendar" element={<ProviderSessionSchedulePage {...authProps}/>} key={Math.random()}/> /* Done */

                    <Route path="/support/tickets" element={<RemoteSupportTicketPage {...authProps} />} key={Math.random()}/> /* Done */
                    <Route path="/support/tickets/:ticketID" element={<RemoteSupportTicketPage {...authProps} />} key={Math.random()}/> /* Done */

                    <Route path="/myLearning/training" element={<ProviderTrainingLandingPage {...authProps} />} /> /* Done */
                    <Route path="/myLearning/continuous-education" element={<EmpContEdLandingPage {...authProps} />} /> /* Done */

                    <Route path="/provider/training/complete" element={<ProviderCompleteTrainingPage {...authProps} />} /> /* Done */

                    <Route path="/training/client-training-dashboard" element={<TrainingDashboardPage {...authProps} />} key={Math.random()}/> /* Done */
                    <Route path="/training/client-training-management" element={<TrainingLandingPage {...authProps} />} key={Math.random()}/> /* Done */
                    <Route path="/training/client-training-management/:status" element={<TrainingLandingPage {...authProps} />}  key={Math.random()}/> /* Done */
                    <Route path="/training/continuous-education-management" element={<ContinuousEducationLandingPage {...authProps} />}  key={Math.random()} /> /* Done */
                    <Route path="/training/continuous-education-management/:status" element={<ContinuousEducationLandingPage {...authProps} />}  key={Math.random()}/> /* Done */
                    <Route path="/training/training-management/create" element={<CreateTrainingPage {...authProps} />} /> /* Done */
                    <Route path="/training/training-management/preview" element={<TrainingPreviewPage {...authProps} />} /> /* Done */
                    <Route path="/training/client-training-management/:trainingID/manage" element={<ManageClientTrainingPage {...authProps} />} /> /* Done */
                    <Route path="/training/continuous-education-management/:trainingID/manage" element={<ManageContinuousEducationPage {...authProps} />} /> /* Done */

                    <Route path="/training/continuous-education-management/create" element={<CreateContinuousTrainingPage {...authProps} />} /> /* Done */

                    <Route path="/provider/training/:trainingID/:trainingMode" element={<ProviderCompleteTrainingPage {...authProps} />} /> /* Done */

                    <Route path="/employee/continuous-education/:trainingID/:trainingMode" element={<EmployeeContinuousEducationPage {...authProps} />} /> /* Done */

                    <Route path='/support/admin/ticketManagement' element={<AdminTicketingDashboard {...authProps} />} /> /* Done */

                    <Route path='/client/admin/controlCenter' element={<ControlCenterPage {...authProps} />} /> /* Done */

                    <Route path='/system/schedule/backgroundJobs' element={<BackgroundJobsPage {...authProps} />} /> /* Done */

                    <Route path='/thermal/video' element={<VideoPage />} /> /* Done */

                    <Route path='/thermal/participants/:participantID/:annotationDate/annotate' element={<VideoAnnotationPage />} /> /* Done */

                    <Route path='/thermal/participants' element={<Participants />} /> /* Done */

                    <Route path='/thermal/participants/:participantID' element={<ParticipantDetailPage />} /> /* Done */

                    <Route path="/schedule/supervisor-calendar" element={<SupervisorCalendar {...sharedAttributes} />} /> /* Done */

                  </Routes>
                </Core>

                            {/* <div className="App">
                              <header className="App-header">
                                <img src={logo} className="App-logo" alt="logo" />
                                <form onSubmit={handleSubmit}>
                                  <p>DODD Provider Repor Upload</p>
                                  <input type="file" multiple={true} onChange={handleChange} ref={ref}/>
                                  <button type="submit">Upload</button>
                                </form>
                                
                                <button onClick={handleLogout}>Logout</button>
                                </header>
                            </div> */}
              </AuthenticatedTemplate>
              <UnauthenticatedTemplate>
                            {/* <button onClick={handleLogin}>Login</button> */}
                  <Home pca={pca}/>
              </UnauthenticatedTemplate> 


          </MsalProvider>
    </AppProvider>
   
  
  );
}

const getProducts = async (): Promise<Product[]> => {
  const url = "https://my-container-app.thankfulgrass-c82ee804.eastus.azurecontainerapps.io/products";
  return axios.get<Product[]>(url,{
    validateStatus: function (status) {
        // if this function returns true, exception is not thrown, so
        // in simplest case just return true to handle status checks externally.
        return true;
    }}).then(response => response.data);
}

const addProduct = async (product: Product): Promise<Product[]> => {
  const url = "https://my-container-app.thankfulgrass-c82ee804.eastus.azurecontainerapps.io/products";
  return axios.post<Product[]>(url, product,{
    validateStatus: function (status) {
        // if this function returns true, exception is not thrown, so
        // in simplest case just return true to handle status checks externally.
        return true;
    }}).then(response => response.data);
}

const updateProduct = async (product: Product): Promise<Product[]> => {
  const url = `https://my-container-app.thankfulgrass-c82ee804.eastus.azurecontainerapps.io/products/${product.id}`;
  return axios.put<Product[]>(url, product,{
    validateStatus: function (status) {
        // if this function returns true, exception is not thrown, so
        // in simplest case just return true to handle status checks externally.
        return true;
    }}).then(response => response.data);
}

const deleteProduct = async (product: Product): Promise<Product[]> => {
  const url = `https://my-container-app.thankfulgrass-c82ee804.eastus.azurecontainerapps.io/products/${product.id}`;
  return axios.delete<Product[]>(url,{
    validateStatus: function (status) {
        // if this function returns true, exception is not thrown, so
        // in simplest case just return true to handle status checks externally.
        return true;
    }}).then(response => response.data);
}


export default App;

